import { getErrorMessage, normalizeDiscountCodeName } from "@ac-expo/utils";
import { isAxiosError } from "axios";
import { FieldValues, Path, UseFormSetError } from "react-hook-form";

export const DUPLICATE_DISCOUNT_CODE_NAME_MESSAGE =
  "Ce nom de code promo est déjà utilisé";

const NAME_400_PATTERN = /nom du code|le code ne peut/i;

export type DiscountCodeNameError = {
  message: string;
  // Label rejected by the backend (409 body), when it could be attributed.
  conflictingName?: string;
};

// Recognises a backend error about a promo code label: 409 duplicate, or 400 format.
// Returns undefined for any other error.
export const getDiscountCodeNameError = (
  error: unknown
): DiscountCodeNameError | undefined => {
  if (!isAxiosError(error)) return undefined;

  const status = error.response?.status;
  const message = getErrorMessage(error, DUPLICATE_DISCOUNT_CODE_NAME_MESSAGE);

  if (status === 409) {
    const conflictingName = error.response?.data?.conflictingName;
    return {
      message,
      ...(typeof conflictingName === "string" ? { conflictingName } : {}),
    };
  }
  if (status === 400 && NAME_400_PATTERN.test(message)) {
    return { message };
  }
  return undefined;
};

// Marks the name field of a single-code form when the backend rejected the label.
export const setDiscountCodeNameError = <TForm extends FieldValues>(
  setError: UseFormSetError<TForm>,
  name: Path<TForm>,
  error: unknown
): void => {
  const nameError = getDiscountCodeNameError(error);
  if (nameError) setError(name, { message: nameError.message });
};

// Marks, in a form holding several promo codes, the name field whose value matches the
// label rejected by the backend. Does nothing when the 409 carries no label (race fallback).
export const setDiscountCodesNameError = <TForm extends FieldValues>(
  setError: UseFormSetError<TForm>,
  arrayName: Path<TForm>,
  discountCodes: { name: string }[],
  error: unknown
): void => {
  const nameError = getDiscountCodeNameError(error);
  if (!nameError?.conflictingName) return;

  const conflictingName = normalizeDiscountCodeName(nameError.conflictingName);
  const index = discountCodes.findIndex(
    (code) => normalizeDiscountCodeName(code.name) === conflictingName
  );
  if (index === -1) return;

  setError(`${arrayName}.${index}.name` as Path<TForm>, {
    message: nameError.message,
  });
};
